import React, {
  useRef, useState, useEffect, useContext, useCallback
} from 'react';
import { string, shape } from 'prop-types';
import { ExperienceContext, useStore, useMVCSID } from '@thd-nucleus/experience-context';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import {
  extend, QueryProvider,
} from '@thd-nucleus/data-sources';
import { Button } from '@thd-olt-component-react/core-ui';
import {
  PodSection,
  PodSpacer,
  ProductImage, ProductPod,
  ProductRating
} from '@thd-olt-component-react/product-pod';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { hasAnchorSku, isExperienceDataResolved, triggerNewRelic } from '../../utils/helpers';
import { makeApiRequest } from '../../utils/requestHelpers';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import './SponsoredProductListing.scss';
import { carouselTypes, PRODUCT } from '../../constants/constants';
import { newRelicConstants } from '../../constants';

const HYDRATION_STANDARD_SCROLL_BUFFER = 750;
const plaPrefix = 'sponsored-pip-pla';

const SponsoredProductListing = (props) => {
  const [adProducts, setAdProducts] = useState([]);
  const [onClickUrl, setOnClickUrl] = useState('');
  const experienceContext = useContext(ExperienceContext);
  const plaChannel = experienceContext.channel;
  let { pageContext, dataSource } = props;
  const schema = 'pip_sponsored';
  const browserId = useMVCSID();
  const { storeId, isLocalized } = useStore({
    varnish: true,
    online: true
  });
  const plaRef = useRef();

  useEffect(() => {
    initImpressionWatchers();
  }, []);

  useEffect(() => {
    if (plaRef.current) {
      plaRef.current.setAttribute('onview-beacon-nucleus', adProducts[0].onViewBeacon.replace('https:', ''));
    }
  }, [plaRef.current]);

  const handleProductClick = useCallback((evt) => {
    evt.preventDefault();
    window.location = onClickUrl;
    triggerNewRelic(newRelicConstants.PRODUCT_LISTING_AD, newRelicConstants.CLICK);
  }, [onClickUrl]);

  useEffect(() => {
    const isResolvedContext = isExperienceDataResolved({ isLocalized, experienceContext });
    if (hasAnchorSku(pageContext) && isResolvedContext && browserId) {
      makeApiRequest({
        browserId,
        schema,
        experienceContext,
        pageContext,
        setAdProducts,
        carouselType: carouselTypes.PLA,
        show: PRODUCT
      });
    }
  }, [isLocalized, experienceContext.mounted, browserId]);

  useEffect(() => {
    if (adProducts.length > 0) {
      const product = adProducts[0];
      const beacon = product.onClickBeacon;
      let productPage = 'https://www.homedepot.com' + product.canonicalURL;
      productPage += (productPage.indexOf('?') > -1) ? '&' : '?';
      productPage += `ITC=AUC-${product.campaignId}-${product.placementId}-${product.slotId}`;
      if (beacon) {
        setOnClickUrl(beacon + productPage);
      } else {
        setOnClickUrl(productPage);
      }
    }
  }, [adProducts]);

  return (
    <>
      {adProducts.length > 0 && (
        <div
          className={`${plaPrefix}-container ${plaChannel}`}
          data-onview-beacon-nucleus={adProducts[0].onViewBeacon.replace('https:', '')}
          ref={plaRef}
          data-component="SponsoredProductListing"
          data-testid="sponsored-product-listing"
        >
          <div
            className={`${plaPrefix}-content ${plaChannel}`}
          >
            <div className={`${plaPrefix}-pod ${plaChannel}`}>
              <QueryProvider
                cacheKey="pla-on-pip"
                dataSources={dataSource}
                defaultVariables={{ storeId }}
              >
                <ProductPod
                  itemId={adProducts[0].id.toString()}
                  analyticsData={{ parent: 'pla-on-pip', position: 1 }}
                  render={(pod) => (
                    <>
                      <ProductImage
                        itemId={pod.itemId}
                        showSecondaryImage={pod.showSecondaryImage}
                        onClick={handleProductClick}
                      />
                      <PodSection columnAlign alignTop>
                        <PodSpacer padding={['TOP', 'BOTTOM']}>
                          <ProductHeader
                            brand="above"
                            itemId={pod.itemId}
                            brandTitleMaxLine={3}
                            disableShopThisCollection
                            onClick={handleProductClick}
                          />
                        </PodSpacer>
                        <ProductRating
                          itemId={pod.itemId}
                          onClick={handleProductClick}
                        />
                        <PodSpacer minHeight="60px">
                          <Price
                            itemId={pod.itemId}
                            large={false}
                            storeId={storeId}
                            displayEachUom={false}
                            onSavingsCenterToggle={pod.onSavingsCenterToggle}
                            hideBadge
                          />
                        </PodSpacer>
                      </PodSection>
                    </>
                  )}
                />
              </QueryProvider>
              <div className={`${plaPrefix}-shop-now-wrapper ${plaChannel}`}>
                <Button
                  className={`${plaPrefix}-shop-now-btn ${plaChannel}`}
                  outline
                  onClick={() => {
                    window.location = onClickUrl;
                    triggerNewRelic(newRelicConstants.PRODUCT_LISTING_AD, newRelicConstants.CLICK);
                  }}
                >
                  Shop Now
                </Button>
              </div>
            </div>
            <p className={`${plaPrefix}-sponsored-tag ${plaChannel}`}>Sponsored</p>
          </div>
        </div>
      )}
    </>
  );
};

const propTypes = {
  pageContext: shape({
    label: string,
    data: shape({
      itemId: Number
    })
  }),
  dataSource: string
};

const defaultProps = {
  pageContext: {},
  dataSource: 'catalog'
};

ProductPod.dataModel = extend(
  ProductImage,
  Price,
  ProductRating,
  ProductHeader,
);

SponsoredProductListing.propTypes = propTypes;
SponsoredProductListing.defaultProps = defaultProps;

SponsoredProductListing.dataModel = extend({}, ProductPod);

const HydratedSponsoredProductListing = withHydrator({
  id: `hydrated-${plaPrefix}`,
  className: `${plaPrefix}-hydrator`,
  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
}, SponsoredProductListing);

const DynamicSponsoredProductListing = withDynamicComponent(HydratedSponsoredProductListing);
const SponsoredProductListingAd = withErrorBoundary(DynamicSponsoredProductListing);

SponsoredProductListingAd.propTypes = propTypes;
SponsoredProductListingAd.defaultProps = defaultProps;

export { SponsoredProductListing, SponsoredProductListingAd };
